import { useEffect, useState } from 'react';
import { api } from '../api';
import type { AccessInfo, AdminUser, Platform } from '../types';

interface Props {
  adminPassword?: string;
  onNotify: (message: string, type: 'success' | 'error') => void;
}

const platformLabels: Record<Platform, string> = {
  max: 'MAX',
  telegram: 'Telegram',
  dev: 'Dev',
};

function formatAdminLabel(admin: AdminUser): string {
  const name = admin.displayName?.trim();
  const base = name || `ID ${admin.userId}`;
  return `${base} (${platformLabels[admin.platform]})`;
}

export function AccessTab({ adminPassword, onNotify }: Props) {
  const [info, setInfo] = useState<AccessInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [newUserId, setNewUserId] = useState('');
  const [newPlatform, setNewPlatform] = useState<Platform>('max');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  async function loadAccess() {
    setLoading(true);
    try {
      const data = await api.adminGetAccess(adminPassword);
      setInfo(data);
      if (data.currentUser.platform !== 'dev') {
        setNewPlatform(data.currentUser.platform);
      }
    } catch (e) {
      onNotify(e instanceof Error ? e.message : 'Не удалось загрузить доступ', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminPassword]);

  async function handleAddAdmin() {
    const userId = Number(newUserId.trim());
    if (!userId || Number.isNaN(userId)) {
      onNotify('Укажите корректный ID пользователя', 'error');
      return;
    }

    setSaving(true);
    try {
      await api.adminAddAccessAdmin(
        { platform: newPlatform, userId, displayName: newDisplayName.trim() || undefined },
        adminPassword
      );
      setNewUserId('');
      setNewDisplayName('');
      await loadAccess();
      onNotify('Администратор добавлен', 'success');
    } catch (e) {
      onNotify(e instanceof Error ? e.message : 'Ошибка добавления', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddMe() {
    setSaving(true);
    try {
      await api.adminAddAccessAdminMe(adminPassword);
      await loadAccess();
      onNotify('Вы добавлены в список администраторов', 'success');
    } catch (e) {
      onNotify(e instanceof Error ? e.message : 'Ошибка добавления', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(id: number) {
    if (!window.confirm('Удалить администратора из списка?')) return;

    setSaving(true);
    try {
      await api.adminRemoveAccessAdmin(id, adminPassword);
      await loadAccess();
      onNotify('Администратор удалён', 'success');
    } catch (e) {
      onNotify(e instanceof Error ? e.message : 'Ошибка удаления', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();

    if (newPassword.length < 6) {
      onNotify('Новый пароль — минимум 6 символов', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      onNotify('Пароли не совпадают', 'error');
      return;
    }

    setSaving(true);
    try {
      const result = await api.adminChangePassword(
        {
          currentPassword: currentPassword || undefined,
          newPassword,
        },
        adminPassword
      );
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onNotify(result.message, 'success');
    } catch (e) {
      onNotify(e instanceof Error ? e.message : 'Не удалось сменить пароль', 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="loading">Загрузка…</div>;
  }

  if (!info) {
    return <p className="subtitle">Не удалось загрузить настройки доступа.</p>;
  }

  const { currentUser } = info;
  const canManage = currentUser.canManageAccess;

  return (
    <>
      <div className="card">
        <h2>Администраторы</h2>
        <p className="subtitle">
          {canManage
            ? 'Супер-админы могут добавлять коллег без доступа к серверу. ID узнать: команда /myid в боте.'
            : 'Список сотрудников с доступом к админке. Добавлять новых может супер-админ.'}
        </p>

        <ul className="admin-list">
          {info.admins.map((admin) => (
            <li key={admin.id} className="admin-list-item">
              <div className="admin-list-item-info">
                <strong>{formatAdminLabel(admin)}</strong>
                <div className="subtitle admin-list-meta">
                  ID {admin.userId}
                  {admin.role === 'super' ? ' · супер-админ' : ''}
                  {admin.isEnvLocked ? ' · из .env' : ''}
                  {admin.addedByName ? ` · добавил ${admin.addedByName}` : ''}
                </div>
              </div>
              {canManage && !admin.isEnvLocked && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={saving}
                  onClick={() => handleRemove(admin.id)}
                >
                  Удалить
                </button>
              )}
            </li>
          ))}
        </ul>

        {canManage && (
          <>
            <div className="field">
              <label>Платформа</label>
              <select value={newPlatform} onChange={(e) => setNewPlatform(e.target.value as Platform)}>
                <option value="max">MAX</option>
                <option value="telegram">Telegram</option>
                {import.meta.env.DEV && <option value="dev">Dev</option>}
              </select>
            </div>
            <div className="field">
              <label>ID пользователя</label>
              <input
                inputMode="numeric"
                placeholder="Например: 556484657"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Имя (необязательно)</label>
              <input
                placeholder="Иван"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
              />
            </div>
            <div className="btn-row">
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving || !newUserId.trim()}
                onClick={handleAddAdmin}
              >
                Добавить
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={saving}
                onClick={handleAddMe}
              >
                Добавить меня ({currentUser.userId})
              </button>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>Пароль админки</h2>
        <p className="subtitle">
          Общий пароль для входа сотрудников без ID в списке.
          {currentUser.isEnvSuperAdmin &&
            ' Супер-админ из .env может сменить пароль без ввода текущего.'}
        </p>
        <form onSubmit={handleChangePassword}>
          {!currentUser.isEnvSuperAdmin && (
            <div className="field">
              <label>Текущий пароль</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
          )}
          <div className="field">
            <label>Новый пароль</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div className="field">
            <label>Повтор нового пароля</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving || !newPassword}>
            Сохранить пароль
          </button>
        </form>
      </div>
    </>
  );
}
